import * as Chartist from 'chartist'
import linePatterns from '@/assets/images/line-patterns.svg?raw'

export function drawLines(pieChart: Chartist.PieChart, labelOffset: number = 32) {
	const labelDelay = 40,
		labelDuration = 300
	const sliceDuration = 450

	const slices: {
		center: DOMPoint
		rekt: DOMRect
	}[] = []

	let seq = 0

	pieChart.on('created', (e) => {
		slices.splice(0, slices.length)
		seq = 0

		const tempContainer = document.createElement('div')
		tempContainer.innerHTML = linePatterns.trim()
		const defs = tempContainer.querySelector('defs')
		if (defs) {
			e.svg.getNode().prepend(defs)
		}
	})

	pieChart.on('draw', (e) => {
		seq++

		if (e.type === 'slice') {
			const node = e.element.getNode<SVGPathElement>()
			const half = node.getTotalLength() / 2
			let point = new DOMPoint()
			try {
				point = node.getPointAtLength(half)
			} catch {
				// ignore
			}

			slices.push({
				center: point,
				rekt: new DOMRect(),
			})
		} else if (e.type === 'label') {
			function resolveOverlaps(rekt: DOMRect, dir: DOMPoint) {
				for (let i = 0; i < slices.length; i++) {
					if (i === e.index) continue

					let overlapping = false
					for (let j = 0; j < 100; j++) {
						overlapping = intersect(rekt, slices[i].rekt)
						if (overlapping) {
							rekt.x += dir.x * 8
							rekt.y += dir.y * 8
						} else {
							break
						}
					}
				}
			}

			// @ts-ignore
			if (e.series?.value < 1) {
				e.element.addClass('d-none')
				return
			}

			const paths = [e.element]

			const chartCenter = new DOMPoint(e.chartRect.width() / 2, e.chartRect.height() / 2)
			const rekt = e.element.getNode().getBoundingClientRect()
			const halfWidth = rekt.width / 2
			const halfHeight = rekt.height / 2

			rekt.x = parseFloat(e.element.attr('dx')!) - halfWidth
			rekt.y = parseFloat(e.element.attr('dy')!) - halfHeight

			if (rekt.x + halfWidth < e.chartRect.width() / 2) rekt.x -= halfWidth
			else rekt.x += halfWidth
			if (rekt.y + halfHeight < e.chartRect.height() / 2) rekt.y -= halfHeight
			else rekt.y += halfHeight

			const dir = new DOMPoint(slices[e.index].center.x - chartCenter.x, slices[e.index].center.y - chartCenter.y)
			const length = Math.sqrt(dir.x * dir.x + dir.y * dir.y)
			dir.x /= length
			dir.y /= length

			rekt.x += dir.x * labelOffset
			rekt.y += dir.y * labelOffset

			resolveOverlaps(rekt, dir)
			slices[e.index].rekt = rekt

			e.element.attr({
				dx: rekt.x + halfWidth,
				dy: rekt.y + halfHeight,
			})

			const underline = new Chartist.Svg(
				'line',
				{
					x1: rekt.x,
					y1: rekt.y + rekt.height,
					x2: rekt.x + rekt.width,
					y2: rekt.y + rekt.height,
				},
				'ct-label-line',
				e.group,
			)
			paths.push(underline)

			const sliceCenter = slices[e.index].center
			const line = new Chartist.Svg(
				'line',
				{
					x1: sliceCenter.x,
					y1: sliceCenter.y,
					x2: sliceCenter.x >= chartCenter.x ? rekt.x : rekt.x + rekt.width,
					y2: rekt.y + rekt.height,
				},
				'ct-label-line',
				e.group,
			)
			paths.push(line)

			paths.forEach((x) =>
				x.animate({
					opacity: {
						begin: seq * labelDelay,
						dur: labelDuration,
						from: 0,
						to: 1,
					},
				}),
			)
		}
	})

	pieChart.on('draw', (data) => {
		if (data.type === 'slice') {
			// Get the total path length in order to use for dash array animation
			const pathLength = data.element.getNode<SVGGeometryElement>().getTotalLength()

			// Set a dasharray that matches the path length as prerequisite to animate dashoffset
			data.element.attr({
				'stroke-dasharray': pathLength + 'px ' + pathLength + 'px',
			})

			// Create animation definition while also assigning an ID to the animation for later sync usage
			const animationDefinition: Record<string, Chartist.AnimationDefinition> = {
				'stroke-dashoffset': {
					id: 'anim' + data.index,
					dur: sliceDuration,
					from: -pathLength + 'px',
					to: '0px',
					//easing: Chartist.easings.easeOutQuint,
					// We need to use `fill: 'freeze'` otherwise our animation will fall back to initial (not visible)
					fill: 'freeze',
				},
			}

			// If this was not the first slice, we need to time the animation so that it uses the end sync event of the previous animation
			// if (data.index !== 0) {
			//	animationDefinition['stroke-dashoffset'].begin = 'anim' + (data.index - 1) + '.end'
			// }

			// We need to set an initial value before the animation starts as we are not in guided mode which would do that for us
			data.element.attr({
				'stroke-dashoffset': -pathLength + 'px',
			})

			// We can't use guided mode as the animations need to rely on setting begin manually
			data.element.animate(animationDefinition, false)
		}
	})
}

const intersect = (a: DOMRect, b: DOMRect): boolean => b.right >= a.left && b.left <= a.right && b.top <= a.bottom && b.bottom >= a.top
